/**
 * API Service for handling authentication and search functionality
 * This service provides methods for retrieving authentication tokens and search results
 * from the Recomdo AI API.
 */
import axios from 'axios';

const AUTH_TOKEN_CACHE_KEY = 'authToken';
const STORE_CURRENCY_CACHE_KEY_PREFIX = 'STORE_CURRENCY_CACHE';
const storeCurrencyRequestMap = new Map();

const getStoreCurrencyCacheKey = (API_BASE_URL, clientKey, storeCode) => {
    return `${STORE_CURRENCY_CACHE_KEY_PREFIX}:${API_BASE_URL || ''}:${clientKey || ''}:${storeCode || 'default'}`;
};

/**
 * Get authentication token from API
 *
 * @param {string} API_BASE_URL - Base URL for API
 * @param {string} clientKey - Client key for authentication
 * @param {string} accessKey - Access key for authentication
 * @param {string} accessSecret - Access secret for authentication
 * @returns {Promise<string|null>} - Authentication token or null if error
 *
 * The token is cached in session storage to reduce API calls.
 * If a cached token exists, it will be returned instead of making a new API call.
 */
const getAuthToken = async (API_BASE_URL, clientKey, accessKey, accessSecret) => {
    if (!API_BASE_URL) {
        console.error('API_BASE_URL is required to fetch auth token');
        return null;
    }

    if (!clientKey || !accessKey || !accessSecret) {
        console.error('Authentication credentials (clientKey, accessKey, accessSecret) are required');
        return null;
    }

    const cachedToken = sessionStorage.getItem(AUTH_TOKEN_CACHE_KEY);
    if (cachedToken) {
        return cachedToken;
    }
    try {
        const response = await axios.get(`${API_BASE_URL}/get_token`, {
            headers: {
                'accept': 'application/json',
                'Client-Key': clientKey,
                'Access-Key': accessKey,
                'Access-Secret': accessSecret,
            },
        });

        if (response.status === 200 && response.data?.data && response.data.data['Authorization Key']) {
            const token = response.data.data['Authorization Key'];

            sessionStorage.setItem(AUTH_TOKEN_CACHE_KEY, token);

            return token;
        } else {
            console.error('Invalid response format from auth token API:',
                response.status,
                response.data?.message || 'No message provided');
            return null;
        }
    } catch (error) {
        if (error.response) {
            console.error(`Error fetching auth token: ${error.response.status} ${error.response.statusText}`);
            console.error('Error details:', error.response.data);
        } else if (error.request) {
            console.error('Error fetching auth token: No response received from server');
        } else {
            console.error('Error fetching auth token:', error.message);
        }
        return null;
    }
};

/**
 * Get store currency code from API and cache it in session storage.
 * This is intended to be called once per session and reused by price UI.
 *
 * @param {string} API_BASE_URL
 * @param {string} clientKey
 * @param {string} accessKey
 * @param {string} accessSecret
 * @param {string} [storeCodeParam]
 * @returns {Promise<string|null>} currency code like "USD"
 */
const getStoreCurrency = async (
    API_BASE_URL,
    clientKey,
    accessKey,
    accessSecret,
    storeCodeParam = null
) => {
    if (!API_BASE_URL) {
        console.error('API_BASE_URL is required to fetch store currency');
        return null;
    }

    if (!clientKey || !accessKey || !accessSecret) {
        console.error('Authentication credentials (clientKey, accessKey, accessSecret) are required');
        return null;
    }

    const storeCode = storeCodeParam || window.__VOICE_SEARCH_CONFIG__?.store_code || 'default';
    const cacheKey = getStoreCurrencyCacheKey(API_BASE_URL, clientKey, storeCode);

    try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
            const cachedCurrency = window.sessionStorage.getItem(cacheKey);
            if (cachedCurrency) {
                return JSON.parse(cachedCurrency);
            }
        }
    } catch (_) {
    }

    if (storeCurrencyRequestMap.has(cacheKey)) {
        return storeCurrencyRequestMap.get(cacheKey);
    }

    const requestPromise = (async () => {
        try {
            const authToken = await getAuthToken(API_BASE_URL, clientKey, accessKey, accessSecret);
            if (!authToken) {
                return null;
            }

            const response = await axios.get(`${API_BASE_URL}/rest/stores/${storeCode}`, {
                headers: {
                    'accept': 'application/json',
                    'Client-Key': clientKey,
                    'Authorization': `Bearer ${authToken}`,
                },
            });

            const currencyCode = response?.data?.data?.currency_code || null;
            const roundingEnabled = response?.data?.data?.roundingEnabled || null;
            if (!currencyCode) {
                return null;
            }

            try {
                if (typeof window !== 'undefined' && window.sessionStorage) {
                    window.sessionStorage.setItem(cacheKey, JSON.stringify({
                        currencyCode: currencyCode,
                        roundingEnabled: roundingEnabled
                    }));
                }
            } catch (_) {
            }

            return {currencyCode:currencyCode,roundingEnabled:roundingEnabled};
        } catch (error) {
            if (error.response) {
                console.error(`Error fetching store currency: ${error.response.status} ${error.response.statusText}`);
                console.error('Error details:', error.response.data);
            } else if (error.request) {
                console.error('Error fetching store currency: No response received from server');
            } else {
                console.error('Error fetching store currency:', error.message);
            }
            return null;
        } finally {
            storeCurrencyRequestMap.delete(cacheKey);
        }
    })();

    storeCurrencyRequestMap.set(cacheKey, requestPromise);
    return requestPromise;
};

/**
 * Get search results from API
 *
 * @param {string} keyword - Search keyword
 * @param {string} API_BASE_URL - Base URL for API
 * @param {string} clientKey - Client key for authentication
 * @param {string} accessKey - Access key for authentication
 * @param {string} accessSecret - Access secret for authentication
 * @returns {Promise<Object|null>} - Search results or null if error
 *
 * This function fetches search results based on the provided keyword.
 * It automatically handles authentication by getting a token using getAuthToken.
 */
const getSearchResults = async (keyword, API_BASE_URL, clientKey, accessKey, accessSecret, profile = null) => {
    if (!keyword || keyword.trim() === '') {
        return null;
    }

    if (!API_BASE_URL) {
        console.error('API_BASE_URL is required to fetch search results');
        return null;
    }

    if (!clientKey || !accessKey || !accessSecret) {
        console.error('Authentication credentials (clientKey, accessKey, accessSecret) are required');
        return null;
    }

    try {
        const authToken = await getAuthToken(API_BASE_URL, clientKey, accessKey, accessSecret);
        if (!authToken) {
            console.error('Failed to get authentication token for search');
            return null;
        }

        const storeCode = window.__VOICE_SEARCH_CONFIG__?.store_code || 'default';

        let profilePayload = profile;
        try {
            if (!profilePayload && typeof window !== 'undefined' && window.localStorage) {
                const cached = window.localStorage.getItem('RECOMDO_PROFILE_CACHE');
                if (cached) {
                    try {
                        profilePayload = JSON.parse(cached);
                    } catch (_) {
                        profilePayload = cached;
                    }
                }
            }
        } catch (_) {
        }

        let url = `${API_BASE_URL}/rest/${storeCode}/voice_search/?search_phase=${encodeURIComponent(keyword)}`;
        if (profilePayload) {
            try {
                const profileParam = typeof profilePayload === 'string' ? profilePayload : JSON.stringify(profilePayload);
                url += `&profile=${encodeURIComponent(profileParam)}`;
            } catch (_) {
            }
        }

        const response = await axios.get(
            url,
            {
                headers: {
                    'accept': 'application/json',
                    'Client-Key': clientKey,
                    'Authorization': `Bearer ${authToken}`,
                },
            }
        );

        if (response.data?.data) {
            return response.data.data?.products || [];
        } else {
            console.error('Invalid response format from search API:',
                response.status,
                response.data?.message || 'No message provided');
            return null;
        }
    } catch (error) {
        if (error.response) {
            console.error(`Error fetching search results: ${error.response.status} ${error.response.statusText}`);
            console.error('Error details:', error.response.data);
        } else if (error.request) {
            console.error('Error fetching search results: No response received from server');
        } else {
            console.error('Error fetching search results:', error.message);
        }
        return null;
    }
};

const getRecommendationProducts = async (recommendationItems, API_BASE_URL, clientKey, accessKey, accessSecret, priceAnchor = null) => {
    try {
        if (!Array.isArray(recommendationItems)) {
            return [];
        }
        if (!API_BASE_URL) {
            console.error('API_BASE_URL is required to fetch recommendation products');
            return [];
        }
        if (!clientKey || !accessKey || !accessSecret) {
            console.error('Authentication credentials (clientKey, accessKey, accessSecret) are required for recommendation products');
            return [];
        }
        const storeCode = window.__VOICE_SEARCH_CONFIG__?.store_code || 'default';

        const CACHE_TTL_MS = 40 * 60 * 1000;
        const cacheKey = (() => {
            try {
                const payload = JSON.stringify({
                    items: recommendationItems,
                    priceAnchor: priceAnchor ?? null,
                    storeCode,
                    base: API_BASE_URL,
                    clientKey
                });
                if (typeof btoa === 'function') return 'RECO_PRODUCTS_CACHE_' + btoa(payload);
                return 'RECO_PRODUCTS_CACHE_' + encodeURIComponent(payload);
            } catch (_) {
                return 'RECO_PRODUCTS_CACHE_DEFAULT';
            }
        })();
        try {
            if (typeof window !== 'undefined' && window.sessionStorage) {
                const raw = sessionStorage.getItem(cacheKey);
                if (raw) {
                    const {data, ts} = JSON.parse(raw);
                    if (typeof ts === 'number' && (Date.now() - ts) < CACHE_TTL_MS && Array.isArray(data)) {
                        return data;
                    }
                }
            }
        } catch (_) {
        }

        const authToken = await getAuthToken(API_BASE_URL, clientKey, accessKey, accessSecret);
        if (!authToken) {
            console.error('Failed to get authentication token for recommendation products');
            return [];
        }
        const recommendationParam = encodeURIComponent(JSON.stringify(recommendationItems));
        let url = `${API_BASE_URL}/rest/${storeCode}/voice_search_recommendation/?recommendation=${recommendationParam}`;
        if (priceAnchor !== null && priceAnchor !== undefined && priceAnchor !== '') {
            const pa = encodeURIComponent(String(priceAnchor));
            url += `&price_anchor=${pa}`;
        }
        const response = await axios.get(url, {
            headers: {
                'accept': 'application/json',
                'Client-Key': clientKey,
                'Authorization': `Bearer ${authToken}`,
            },
        });
        if (response.data?.data) {
            const products = response.data.data?.products || [];
            try {
                if (typeof window !== 'undefined' && window.sessionStorage) {
                    sessionStorage.setItem(cacheKey, JSON.stringify({data: products, ts: Date.now()}));
                }
            } catch (_) {
            }
            return products;
        } else {
            console.error('Invalid response format from recommendation search API:',
                response.status,
                response.data?.message || 'No message provided');
            return [];
        }
    } catch (error) {
        if (error.response) {
            console.error(`Error fetching recommendation products: ${error.response.status} ${error.response.statusText}`);
            console.error('Error details:', error.response.data);
        } else if (error.request) {
            console.error('Error fetching recommendation products: No response received from server');
        } else {
            console.error('Error fetching recommendation products:', error.message);
        }
        return [];
    }
};

export {getAuthToken, getStoreCurrency, getSearchResults, getRecommendationProducts};
